<template>
  <div>
    <div
      class="p-1 rounded cursor-pointer board-dp"
      :class="{ 'bg-gray': disabled, 'bg-white': !disabled }"
      style="border: solid 1px #ccc"
      ref="date"
    >
      <div v-if="value">
        <i class="far fa-clock mr-2"></i>
        <span v-if="mode === 'date' && isRange">{{ value | formatRangeDate }}</span>
        <span v-else-if="mode === 'dateTime'">{{ value | formatDateTime }}</span>
        <span v-else>{{ value | formatDateWithoutHours }}</span>
      </div>
      <div v-else>No date set</div>
    </div>

    <b-popover
      :custom-class="customClass"
      ref="datePopover"
      :disabled="disabled"
      :target="() => $refs.date"
      triggers="click blur"
      :placement="popoverPlacement"
      v-on:shown="shown"
      v-on:hidden="hidden"
    >
      <div class="d-flex mb-2" v-if="displayQuickAddHours">
        <button class="btn btn-success btn-sm flex-grow-1" @click="quickAddHours(1)">
          <i class="far fa-clock mr-2"></i>
          + 1h from now
        </button>
        <button class="btn btn-success btn-sm flex-grow-1 ml-2" @click="quickAddHours(8)">
          <i class="far fa-clock mr-2"></i>
          + 8h from now
        </button>
      </div>

      <v-date-picker
        v-model="innerValue"
        trim-weeks
        :mode="mode"
        :minute-increment="5"
        :model-config="{ type: 'string' }"
        is24hr
        :timezone="userTimezone"
        :is-range="isRange"
        :columns="$screens({ default: columns })"
        :min-date="minDate"
        :attributes="attributes"
      ></v-date-picker>

      <div>
        <div class="mt-2 text-center" v-if="this.mode === 'dateTime'">
          <small>
            Time in
            <span class="text-uppercase">{{ userStore.user.timeZone }}</span>
          </small>
        </div>

        <div class="mt-2 d-flex justify-content-between">
          <div>
            <button class="btn btn-secondary btn-sm mr-2" @click="cancel">
              <i class="far fa-times mr-2"></i>
              Cancel
            </button>
            <button class="btn btn-danger btn-sm" @click="removeDate" v-if="!hideDateClearBtn">
              <i class="far fa-trash mr-2"></i>
              Remove
            </button>
          </div>
          <button class="btn btn-success btn-sm" @click="save">
            <i class="far fa-check mr-2"></i>
            Ok
          </button>
        </div>
      </div>
    </b-popover>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import Component from 'vue-class-component';
  import { Prop } from 'vue-property-decorator';
  import { UserModule, UserStore } from '@/store/user';
  import { DateTime } from 'luxon';

  @Component({
    components: {},
  })
  export default class BoardItemDatePicker extends Vue {
    private userStore: UserModule = UserStore;

    @Prop() value: string;
    @Prop() placement;
    @Prop() disabled: boolean;
    @Prop({ default: 'dateTime' }) mode: string;
    @Prop({ default: false }) isRange: boolean;
    @Prop() customClass: string;
    @Prop({ default: 1 }) columns;
    @Prop({ default: null }) minDate;
    @Prop({ default: false }) displayQuickAddHours;
    @Prop({ default: false }) hideDateClearBtn;

    attributes = [
      {
        key: 'today',
        highlight: {
          color: 'white',
          fillMode: 'outline',
        },
        dates: new Date(),
      },
    ];

    private innerValue = '';

    mounted() {
      this.innerValue = this.value;
    }

    get popoverPlacement(): string {
      return this.placement ?? 'right';
    }

    get userTimezone(): string | undefined {
      return this.userStore.user.timeZone == 'local' ? undefined : this.userStore.user.timeZone;
    }

    quickAddHours(hours: number) {
      const now = DateTime.utc();
      this.innerValue = now.plus({ hours: hours }).toISO();
      this.save();
    }

    removeDate() {
      this.$emit('input', null);
      this.closePopover();
    }

    save() {
      this.$emit('input', this.innerValue);
      this.closePopover();
    }

    cancel() {
      this.closePopover();
    }

    closePopover() {
      const popover: any = this.$refs.datePopover;
      if (popover) {
        popover.doClose();
      }
    }

    shown() {
      this.$emit('shown');
    }

    hidden() {
      this.$emit('hidden');
    }
  }
</script>

<style scoped lang="scss">
  .boardOverview {
    display: inline-table;
  }
</style>
